import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CheckBadgeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PushPanelShell from './PushPanelShell';
import type {
  FcmTokenDeviceRow,
  FcmTokenInsights,
  FcmTokenOwnerRow,
} from '../../../models/MasterAdmin/pushNotificationHub';
import {
  fetchFcmOwners,
  fetchFcmTokenInsights,
  fetchOwnerFcmTokens,
  validateOwnerFcmTokens,
} from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

function formatWhen(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
}

function platformLabel(p: string) {
  if (p === 'android') return 'Android';
  if (p === 'ios') return 'iOS';
  if (p === 'web') return 'Web';
  return 'Unknown';
}

const InsightCard: React.FC<{ label: string; value: number | string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
    <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </p>
    {hint ? <p className="mt-1 text-[11px] text-gray-500">{hint}</p> : null}
  </div>
);

const NotifiableBadge: React.FC<{ row: FcmTokenOwnerRow }> = ({ row }) => {
  const platforms = row.notifiablePlatforms?.length
    ? row.notifiablePlatforms.map(platformLabel).join(', ')
    : 'None';
  return (
    <span className="group relative inline-flex cursor-help items-center gap-1">
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
          row.notifiable
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
        }`}
      >
        {row.notifiable ? 'Yes' : 'No'}
      </span>
      <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />
      <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-52 -translate-x-1/2 rounded-lg bg-gray-900 px-2.5 py-2 text-[11px] leading-snug text-white opacity-0 shadow-lg transition group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900">
        {row.notifiable
          ? `Can receive push on: ${platforms}`
          : 'No active FCM token — cannot receive campaign pushes'}
        <span className="mt-1 block text-gray-300 dark:text-gray-600">
          Active devices: {row.activeTokenCount} · Inactive: {row.inactiveTokenCount}
        </span>
      </span>
    </span>
  );
};

const PushFcmTokensPanel: React.FC = () => {
  const [insights, setInsights] = useState<FcmTokenInsights | null>(null);
  const [items, setItems] = useState<FcmTokenOwnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ownerType, setOwnerType] = useState('ALL');
  const [platform, setPlatform] = useState('');
  const [notifiableOnly, setNotifiableOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [days, setDays] = useState(0);
  const [selected, setSelected] = useState<FcmTokenOwnerRow | null>(null);
  const [tokens, setTokens] = useState<FcmTokenDeviceRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchFcmTokenInsights()
      .then((res) => setInsights(res.data))
      .catch(() => setInsights(null));
  }, []);

  useEffect(() => {
    if (selected) return;
    setLoading(true);
    fetchFcmOwners({
      page,
      limit: 30,
      search: search || undefined,
      ownerType: ownerType === 'ALL' ? undefined : ownerType,
      platform: platform || undefined,
      notifiableOnly,
      activeOnly,
      days: days || undefined,
    })
      .then((res) => {
        setItems(res.data.items || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalItems(res.data.pagination?.totalItems || 0);
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to load FCM registrations'));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [page, search, ownerType, platform, notifiableOnly, activeOnly, days, selected]);

  const openOwner = async (row: FcmTokenOwnerRow) => {
    setSelected(row);
    setDetailLoading(true);
    try {
      const res = await fetchOwnerFcmTokens(row.ownerType, row.ownerId);
      setTokens(res.data.tokens || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load devices'));
      setTokens([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const runValidate = async (deactivateInvalid: boolean) => {
    if (!selected) return;
    setValidating(true);
    try {
      const res = await validateOwnerFcmTokens(selected.ownerType, selected.ownerId, deactivateInvalid);
      setTokens(res.data.tokens || []);
      toast.success(
        `Checked ${res.data.validCount + res.data.invalidCount} token(s): ${res.data.validCount} valid, ${res.data.invalidCount} invalid`
      );
      if (deactivateInvalid) {
        const insightsRes = await fetchFcmTokenInsights();
        setInsights(insightsRes.data);
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Validation failed'));
    } finally {
      setValidating(false);
    }
  };

  if (selected) {
    return (
      <PushPanelShell
        title={selected.name}
        description={`${selected.ownerType} · ${selected.ownerId}${selected.phone ? ` · ${selected.phone}` : ''}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={validating}
              onClick={() => void runValidate(false)}
              className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {validating ? 'Checking…' : 'Validate with Firebase'}
            </button>
            <button
              type="button"
              disabled={validating}
              onClick={() => {
                if (window.confirm('Deactivate tokens that Firebase reports as invalid?')) {
                  void runValidate(true);
                }
              }}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-800 dark:text-red-300"
            >
              Validate & deactivate invalid
            </button>
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setTokens([]);
              }}
              className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>
          </div>
        }
      >
        <div className="mb-4 flex flex-wrap gap-3 text-sm">
          <span>
            Notifiable: <strong>{selected.notifiable ? 'Yes' : 'No'}</strong>
          </span>
          <span>
            Platforms:{' '}
            <strong>
              {(selected.notifiablePlatforms.length
                ? selected.notifiablePlatforms
                : selected.platforms
              )
                .map(platformLabel)
                .join(', ') || '—'}
            </strong>
          </span>
          <span>
            Last used: <strong>{formatWhen(selected.lastUsedAt)}</strong>
          </span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Device</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Platform</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Active</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Token</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Firebase</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last used</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {detailLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Loading devices…
                  </td>
                </tr>
              ) : tokens.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    No tokens found.
                  </td>
                </tr>
              ) : (
                tokens.map((t) => (
                  <tr key={t.id}>
                    <td className="px-4 py-3 font-mono text-xs">{t.deviceId}</td>
                    <td className="px-4 py-3">{platformLabel(t.platform)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          t.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {t.tokenPreview}
                      {!t.formatOk ? (
                        <span className="ml-1 text-red-600">(bad format)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.validation?.valid === true ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckBadgeIcon className="h-4 w-4" /> Valid
                        </span>
                      ) : t.validation?.valid === false ? (
                        <span className="text-red-600" title={t.validation.message || ''}>
                          Invalid{t.validation.code ? ` · ${t.validation.code}` : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400">Not checked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(t.lastUsedAt)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(t.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Validation uses Firebase dry-run (no notification is delivered). Invalid tokens can be
          deactivated so campaigns skip them.
        </p>
      </PushPanelShell>
    );
  }

  const totals = insights?.totals;

  return (
    <PushPanelShell
      title="FCM token inventory"
      description="See who can receive push: users, vendors, and anonymous devices — with platforms, activity, and Firebase validation."
    >
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <InsightCard label="Active tokens" value={totals?.activeTokens || 0} />
        <InsightCard label="Inactive tokens" value={totals?.inactiveTokens || 0} />
        <InsightCard label="Notifiable users" value={totals?.notifiableUsers || 0} />
        <InsightCard label="Notifiable vendors" value={totals?.notifiableVendors || 0} />
        <InsightCard label="Anonymous devices" value={totals?.guestTokens || 0} hint="No platform stored" />
        <InsightCard
          label="Platform mix"
          value={`${insights?.byPlatform?.android || 0} / ${insights?.byPlatform?.ios || 0} / ${insights?.byPlatform?.web || 0}`}
          hint="Android / iOS / Web (active)"
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search name, phone, id, device…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput.trim());
              setPage(1);
            }
          }}
          className="min-w-[220px] rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
        <button
          type="button"
          onClick={() => {
            setSearch(searchInput.trim());
            setPage(1);
          }}
          className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900"
        >
          Search
        </button>
        <select
          value={ownerType}
          onChange={(e) => {
            setOwnerType(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="ALL">All types</option>
          <option value="USER">Users</option>
          <option value="VENDOR">Vendors</option>
          <option value="GUEST">Anonymous</option>
        </select>
        <select
          value={platform}
          onChange={(e) => {
            setPlatform(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="">All platforms</option>
          <option value="android">Android</option>
          <option value="ios">iOS</option>
          <option value="web">Web</option>
          <option value="unknown">Unknown (guests)</option>
        </select>
        <select
          value={days}
          onChange={(e) => {
            setDays(Number(e.target.value));
            setPage(1);
          }}
          className="rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value={0}>Any activity time</option>
          <option value={7}>Active last 7 days</option>
          <option value={30}>Active last 30 days</option>
          <option value={90}>Active last 90 days</option>
        </select>
        <label className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm dark:border-gray-600">
          <input
            type="checkbox"
            checked={notifiableOnly}
            onChange={(e) => {
              setNotifiableOnly(e.target.checked);
              setPage(1);
            }}
          />
          Notifiable only
        </label>
        <label className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm dark:border-gray-600">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
          />
          Has active token
        </label>
      </div>

      <p className="mb-2 text-xs text-gray-500">
        {totalItems.toLocaleString()} owners · hover Notifiable for reachable platforms
      </p>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Owner</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Notifiable</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Platforms</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last used</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">First seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  No FCM registrations match these filters.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={`${row.ownerType}:${row.ownerId}`}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40"
                  onClick={() => void openOwner(row)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{row.name}</p>
                    <p className="text-xs text-gray-500">
                      {row.phone || row.ownerId}
                      {row.email ? ` · ${row.email}` : ''}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs font-medium">{row.ownerType}</td>
                  <td className="px-4 py-3">
                    <NotifiableBadge row={row} />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(row.platforms || []).map(platformLabel).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="text-emerald-700">{row.activeTokenCount} active</span>
                    {row.inactiveTokenCount > 0 ? (
                      <span className="text-gray-400"> · {row.inactiveTokenCount} off</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.lastUsedAt)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.firstRegisteredAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushFcmTokensPanel;
